import React from 'react';
import {
  Action,
  ConfirmModal,
  FormHandlers,
  Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { ShootingStatus } from '../../../domain/types';
import { Shooting } from '../../../providers/api/dtos';
import { updateShooting } from '../../../providers/api/shooting/shootingProvider';
import { useComputedShootingStatus } from '../../hooks/use-computed-shooting-status/useComputedShootingStatus';
import UpsertShootingForm, { UpsertShootingFormModel } from '../../components/upsert-shooting-form/UpsertShootingForm';
import { shootingToUpsertShootingFormModel, upsertShootingFormModelToShooting } from '../../utils';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import usePhotographer from '../../hooks/use-photographer/usePhotographer';
import { upsertShootingValidationSchema } from '../../components/upsert-shooting-form/UpsertShootingForm.schema';

interface ChildFnProps {
  isSaving: boolean,
  markAsNotExecuted: VoidFunction,
}

export interface MarkShootingAsNotExecutedActionProps extends React.AriaAttributes {
  shooting: Shooting,
  onSuccess?: (shooting: Shooting) => void,
  children?: (props: ChildFnProps) => React.ReactNode
}

export const MARK_SHOOTING_AS_NOT_EXECUTED_ERROR_MESSAGE = 'Non è stato possibile segnare lo shooting come non eseguito.';
export const MARK_SHOOTING_AS_NOT_EXECUTED_SUCCESS_MESSAGE = 'Lo shooting è stato segnato come non eseguito con successo.';

const MarkShootingAsNotExecutedAction: React.FC<MarkShootingAsNotExecutedActionProps> = ({
  shooting,
  onSuccess,
  children,
  ...rest
}) => {
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const [isSaving, setIsSaving] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState<1 | 2>(1);

  const { userIsContentEditor, userIsAdmin } = useRBAC();
  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const { data } = useComputedShootingStatus(shooting);
  const { data: photographer } = usePhotographer(shooting.photographerId);

  const canBeMarkedAsNotExecuted = data?.canBeUpdatedTo(ShootingStatus.NOT_EXECUTED);

  const closeModal = React.useCallback(() => {
    modal.close();
    setCurrentStep(1);
  }, [modal]);

  const confirmCancel = React.useCallback(async (formValues: UpsertShootingFormModel) => {
    setIsSaving(true);

    try {
      const shootingToMarkAsNotExecuted: Shooting = upsertShootingFormModelToShooting(formValues, {
        ...shooting,
        projectStatus: ShootingStatus.NOT_EXECUTED,
      });

      const shootingMarkedAsNotExecuted = await updateShooting(shootingToMarkAsNotExecuted, shooting);

      setIsSaving(false);
      closeModal();

      addSuccess(MARK_SHOOTING_AS_NOT_EXECUTED_SUCCESS_MESSAGE);
      onSuccess?.(shootingMarkedAsNotExecuted);
    } catch (error) {
      addError(MARK_SHOOTING_AS_NOT_EXECUTED_ERROR_MESSAGE);
      setIsSaving(false);
    }
  }, [shooting, closeModal, addSuccess, onSuccess, addError]);

  const handleConfirm = React.useCallback(() => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else {
      formRef.current?.handleSubmit();
    }
  }, [currentStep]);

  if (!canBeMarkedAsNotExecuted) {
    return null;
  }

  return (
    <>
      {children
        ? children({
          isSaving,
          markAsNotExecuted: modal.open,
        })
        : (
          <Action
            label="Segna come non eseguito"
            aria-label="Pulsante per segnare lo shooting come non eseguito"
            onClick={() => modal.open()}
            loading={isSaving}
            {...rest}
          />
        )}
      <Portal>
        <ConfirmModal
          isOpen={modal.isOpen}
          title="Conferma shooting non eseguito"
          aria-label="Conferma shooting non eseguito"
          onConfirm={handleConfirm}
          variant="critical"
          onAbort={closeModal}
          loading={isSaving}
        >
          {currentStep === 1 && "Confermando l'operazione lo shooting sarà segnato come non eseguito."}
          {currentStep === 2 && (
            <UpsertShootingForm
              initialValues={shootingToUpsertShootingFormModel(shooting, photographer)}
              innerRef={formRef}
              onSubmit={confirmCancel}
              loading={isSaving}
              validationSchema={upsertShootingValidationSchema(shooting)}
              hiddenFields={(!userIsContentEditor && !userIsAdmin) ? {
                virtualTourType: true,
                deliveryDate: true,
                photoPrice: true,
                expensesRefund: true,
                extraServicesPrice: true,
              } : undefined}
              disabledFields={{
                type: true,
                photographer: true,
                date: true,
                time: true,
                virtualTourType: true,
                deliveryDate: true,
                photoPrice: true,
                expensesRefund: true,
                extraServicesPrice: true,
              }}
              size="S"
            />
          )}
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default MarkShootingAsNotExecutedAction;
